import React, { useMemo } from 'react';
import type { ReactNode } from 'react';

import { EntryCardPromoTag } from '@/components/entry-card';
import { ds } from '@/components/lib/design.system';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { ComputedEntryPromotion } from '@/feature/entries-pickem/hooks/use-compute-entry-promos';
import { RaceToPlaceInfo } from '@/feature/lobby/components/race-to-place/RaceToPlaceInfo';
import { MetadataType, Result } from '@/types/api.generated';

/**
 * Builds the standard promo badges (`EntryCardPromoTag`) for a Pick'em entry, shared by the entries
 * list card and the entry details footer. The `betrBucks` promo is excluded - it is surfaced
 * elsewhere (a payout tag on the list card, a dedicated tag in the footer), not as a promo badge.
 * Only entries that are not lost or void show the added profit for promos that have it.
 */
export const usePickemEntryPromos = (entryPromotions: ComputedEntryPromotion[], result?: Result): ReactNode[] => {
    const { showInfoSheet } = useAlerts();
    const isLostOrVoid = result === Result.Loss || result === Result.Void;

    return useMemo(() => {
        return entryPromotions
            .filter(promo => promo.id !== 'betrBucks')
            .map(promo => {
                const addedProfit = promo.metadata?.find(data => data.type === MetadataType.PayoutAmount)?.value;
                const overflow = addedProfit && !isLostOrVoid ? { label: `+$${addedProfit} in Betr Bucks` } : undefined;
                const isRaceToPlace = promo.id === 'raceToPlace';
                const isMysteryOrRaceToPlace = promo.id === 'mystery' || isRaceToPlace;
                const label = isMysteryOrRaceToPlace ? `${promo.name} entry` : `${promo.name} applied`;
                const onPress = isRaceToPlace
                    ? () => {
                          showInfoSheet({
                              backgroundColor: ds.colorValue.background.neutral.base,
                              title: 'Race to Place',
                              description: <RaceToPlaceInfo />,
                              buttonLabel: 'Got it',
                              primaryButtonTestId: 'race-to-place-info-sheet-confirm-button',
                          });
                      }
                    : undefined;
                return <EntryCardPromoTag key={promo.id} label={label} overflow={overflow} onInfoPress={onPress} />;
            });
    }, [entryPromotions, isLostOrVoid, showInfoSheet]);
};
